import { useContext, useEffect, useState } from 'react'
import { MuiOtpInput } from 'mui-one-time-password-input'
import {
  Box, Button, CircularProgress, Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useSelector } from '@xstate/react'
import { LmnContext } from 'src/components/Dialogs/Lmn/LmnDialog'
import styles from 'src/components/Lmn/Lmn.scss'
import { getCustomerInfo, getSmallLoading, getCodeError } from 'src/helpers/lmnHelpers'
import mixpanel from 'mixpanel-browser'
import LmnBackButton from '../common/LmnBackButton'

const setDashInPhoneNumber = (phoneNumber: string): string => {
  const phoneWithoutPrefix = phoneNumber.replace('+1', '')
  const newValueArr = [
    phoneWithoutPrefix.slice(0, 3),
    phoneWithoutPrefix.slice(3, 6),
    phoneWithoutPrefix.slice(6, 10),
  ].filter((x) => x)
  return newValueArr.join('-')
}

const LmnPhoneVerification = (): JSX.Element => {
  const { palette } = useTheme()
  const [code, setCode] = useState('')

  const { lmnContextService } = useContext(LmnContext)
  const { send } = lmnContextService

  const lmnCustomer = useSelector(lmnContextService, getCustomerInfo)
  const loading = useSelector(lmnContextService, getSmallLoading)
  const codeError = useSelector(lmnContextService, getCodeError)

  useEffect(() => {
    mixpanel.track('view_lmn_phone_verification')
  }, [])

  const handleNextStepClicked = (): void => {
    mixpanel.track('completed_lmn_phone_verification')
    send({
      type: 'VERIFY_OTP',
      data: {
        code,
      },
    })
  }

  const validateOtp = (value: string): boolean => {
    const isNumber = typeof value === 'number'
    const isString = typeof value === 'string'
    return (isNumber || (isString && value !== '')) && !Number.isNaN(Number(value))
  }

  const handleResetClicked = (): void => {
    send({ type: 'RESEND_OTP', data: { phone: lmnCustomer.phone } })
  }

  return (
    <>
      <Box display="flex" flexDirection="column" paddingBottom="24px">
        <Typography variant="h4Bold">Verify Phone Number</Typography>
        <Typography variant="body3" color="gray1.main">
          We&apos;ve sent a code to
          <b>
            {' +1 '}
            {setDashInPhoneNumber(lmnCustomer.phone)}
          </b>
          . Please enter it here to
          verify your number.
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column">
        <Typography variant="body2Bold" marginBottom="12px">Verification code</Typography>
        <MuiOtpInput
          autoFocus
          value={code}
          validateChar={validateOtp}
          onChange={(value: string) => setCode(value)}
          length={6}
        // TODO: could use this in the future to auto-submit the code when they complete it
        // onComplete={handleComplete}
        />
      </Box>
      <Box display="felx" flexDirection="row" paddingBottom="24px" marginTop="5px">
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              paddingTop: '5px',
            }}
          >
            <Typography
              color="gray1.main"
              fontWeight="regular"
              variant="body3"
            >
              Sending a new code...
            </Typography>
            <CircularProgress
              color="green1"
              size={20}
            />
          </Box>
        ) : (
          <Button
            sx={{ color: `${palette.gray1.main} !important`, padding: '0px' }}
            variant="text"
            onClick={handleResetClicked}
          >
            <Typography
              color="gray1.main"
              fontWeight="regular"
              variant="body3"
              sx={{
                textDecoration: 'underline !important',
              }}
            >
              Send a new code
            </Typography>
          </Button>
        )}
      </Box>
      {codeError ? (
        <Typography
          color="error.main"
          fontWeight="regular"
          variant="body3"
        >
          Oops, that code is not correct. Please try again.
        </Typography>
      ) : null}
      <Button
        className={styles.lmnButton}
        color="primary"
        fullWidth
        size="large"
        variant="contained"
        onClick={handleNextStepClicked}
      >
        <Typography variant="body2Bold">Continue</Typography>
      </Button>
      <Box display="flex" justifyContent="center">
        <LmnBackButton />
      </Box>
    </>
  )
}

export default LmnPhoneVerification
